import { CheckCircle2, Circle, ClipboardList, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { qaLastReviewDate, qaModuleReviews, qaScenarioGroups, type QaStatus } from "@/lib/qaReview";

const statusLabels: Record<QaStatus, string> = {
  pass: "Pass",
  partial: "Partial",
  fail: "Fail",
  blocked: "Blocked",
  not_run: "Not run",
};

const statusClass: Record<QaStatus, string> = {
  pass: "border-success/30 bg-success/10 text-success",
  partial: "border-warning/30 bg-warning/10 text-warning",
  fail: "border-destructive/30 bg-destructive/10 text-destructive",
  blocked: "border-destructive/30 bg-destructive/10 text-destructive",
  not_run: "border-border bg-muted text-muted-foreground",
};

const priorityClass = {
  P0: "border-destructive/30 bg-destructive/10 text-destructive",
  P1: "border-warning/30 bg-warning/10 text-warning",
  P2: "border-info/30 bg-info/10 text-info",
  P3: "border-border bg-muted text-muted-foreground",
};

export default function AdminQaReviewPage() {
  const totals = qaModuleReviews.reduce(
    (acc, item) => {
      acc[item.completion] = (acc[item.completion] ?? 0) + 1;
      return acc;
    },
    {} as Record<QaStatus, number>,
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">QA Review</h1>
          <p className="text-xs text-muted-foreground">Feature checklist, test status, missing work, and release priorities.</p>
        </div>
        <Badge variant="outline">Last test: {qaLastReviewDate}</Badge>
      </header>

      <main className="space-y-6 p-6">
        <div className="grid gap-4 md:grid-cols-5">
          {(["pass", "partial", "fail", "blocked", "not_run"] as QaStatus[]).map((status) => (
            <Card key={status}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{statusLabels[status]}</p>
                <p className="mt-2 text-3xl font-bold">{totals[status] ?? 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4" />
              Test Scenario Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 lg:grid-cols-2">
            {qaScenarioGroups.map((group) => (
              <div key={group.title} className="rounded-md border border-border p-4">
                <h2 className="font-semibold">{group.title}</h2>
                <div className="mt-3 space-y-2">
                  {group.items.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm">
                      <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Module Review Matrix</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Test</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Missing / Broken</TableHead>
                  <TableHead>Assigned area</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {qaModuleReviews.map((item) => (
                  <TableRow key={item.module}>
                    <TableCell>
                      <div className="font-medium">{item.module}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{item.notes}</div>
                    </TableCell>
                    <TableCell><StatusPill status={item.completion} /></TableCell>
                    <TableCell><StatusPill status={item.testResult} /></TableCell>
                    <TableCell>
                      <span className={cn("inline-flex rounded-full border px-2 py-1 text-xs font-medium", priorityClass[item.priority])}>
                        {item.priority}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <IssueList label="Missing" items={item.missing} />
                      <IssueList label="Broken" items={item.broken} />
                      {item.missing.length === 0 && item.broken.length === 0 && (
                        <div className="flex items-center gap-2 text-sm text-success">
                          <CheckCircle2 className="h-4 w-4" />
                          No open issue in this pass
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs text-xs text-muted-foreground">{item.assignedArea}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatusPill({ status }: { status: QaStatus }) {
  return <span className={cn("inline-flex rounded-full border px-2 py-1 text-xs font-medium", statusClass[status])}>{statusLabels[status]}</span>;
}

function IssueList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-2 space-y-1">
      <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <XCircle className="h-3 w-3" />
        {label}
      </div>
      {items.map((item) => (
        <p key={item} className="text-xs leading-5 text-muted-foreground">{item}</p>
      ))}
    </div>
  );
}

